import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { InsightsMobileClearAllActionSheet } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-clear-all-action-sheet';
import { InsightsMobileHiddenAlertsList } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-hidden-alerts-list';
import { Pressable } from 'react-native';
import { PushView, Typography, XStack } from '@usepepper/sprout-kit-ui';
import { UseAlertPressResult } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-alert-press';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

interface InsightsMobileHiddenAlertsPushViewProps extends UseAlertPressResult {
  alerts: Alert[];
  clearing: boolean;
  onClearAll: () => void;
  onClose: () => void;
  onDelete: (alert: Alert) => void;
  onRestore: (alert: Alert) => void;
  open: boolean;
}

export const InsightsMobileHiddenAlertsPushView: React.FC<
  InsightsMobileHiddenAlertsPushViewProps
> = ({
  alerts,
  clearing,
  onAccountDetailPress,
  onAlertPress,
  onClearAll,
  onClose,
  onDelete,
  onRestore,
  open,
}) => {
  const insets = useSafeAreaInsets();
  const theme = useTheme();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <PushView
      aria-label={i18n.t('insights_hub.hidden_alerts.title')}
      onOpenChange={handleOpenChange}
      open={open}
    >
      <PushView.Header
        backLabel={i18n.t('back')}
        rightAction={
          <Pressable
            accessibilityRole="button"
            onPress={() => setConfirmClearOpen(true)}
          >
            <XStack paddingRight="$8">
              <Typography
                bold
                color="$textPrimary"
                numberOfLines={1}
                size="$sm"
              >
                {i18n.t('insights_hub.hidden_alerts.clear_all')}
              </Typography>
            </XStack>
          </Pressable>
        }
      >
        <PushView.Title>
          {i18n.t('insights_hub.hidden_alerts.title')}
        </PushView.Title>
      </PushView.Header>
      <PushView.Body padding="$0">
        <InsightsMobileHiddenAlertsList
          alerts={alerts}
          bottomInset={insets.bottom + theme.sk.spacing.spaceMedium}
          onAccountDetailPress={onAccountDetailPress}
          onAlertPress={onAlertPress}
          onDelete={onDelete}
          onRestore={onRestore}
        />
      </PushView.Body>

      <InsightsMobileClearAllActionSheet
        loading={clearing}
        onCancel={() => setConfirmClearOpen(false)}
        onConfirm={() => {
          setConfirmClearOpen(false);
          onClearAll();
        }}
        open={confirmClearOpen}
      />
    </PushView>
  );
};
